import { useMemo } from 'react'
import {
  defaultUserGroupsSelector,
  useAuthenticatedUser,
  useUserGroups,
  useUsersByID,
} from '@opallabs/api'
import {
  readableResourcesAsInheritanceResources,
  type InheritanceResource,
  type ReadableAccessMetadata,
  type User,
} from '@opallabs/concepts'
import { useWorkspace } from '@opallabs/hooks'
import { getMemberCount } from './helpers'
import { type PermUserGroup, type PermUserLike } from './types'

type UseComputedAccessArgs = Readonly<{
  accessMetadata?: ReadableAccessMetadata
  ownerId?: string
}>

/**
 * Processes `ReadableAccessMetadata` into useful subsets. For example: `collaborators` is a unique array of users from
 * all the groups and directly-added users, `owner` is split out from the other collaborators, access `resources` is
 * split into `inheritanceResources` and `workspaceResource`, and so on.
 *
 * Callers that need editable collaborator/resource state should use the `useEditableAccessMembers` wrapper, or (if
 * absolutely necessary) manually handle their own `useState`.
 */
export function useComputedAccess({
  accessMetadata,
  ownerId,
}: UseComputedAccessArgs) {
  const currentWorkspace = useWorkspace()
  const currentUserQuery = useAuthenticatedUser({
    routeWorkspaceSlug: currentWorkspace.slug,
  })

  const allGroupsResult = useUserGroups({
    enabled: accessMetadata != null && accessMetadata.groups.length > 0,
    filter: {
      members: {
        workspace_id: [currentWorkspace.uuid],
      },
    },
    select: (data) => defaultUserGroupsSelector(data, false),
  })

  // @note Ensure owner is in the list of fetched records, in case they weren't in `accessMetadata.users`
  const userIdsIncludingOwner = useMemo(() => {
    const ids = new Set(accessMetadata?.users.map((user) => user.id) ?? [])
    if (ownerId) ids.add(ownerId)
    return [...ids]
  }, [accessMetadata?.users, ownerId])

  const collaboratorUsersResult = useUsersByID({
    enabled: userIdsIncludingOwner.length > 0,
    userIds: userIdsIncludingOwner,
  })

  const isLoading =
    allGroupsResult.isInitialLoading ||
    collaboratorUsersResult.isInitialLoading ||
    currentUserQuery.isInitialLoading

  const { collaborators, owner } = useMemo(() => {
    let owner: User | undefined
    const collaborators: PermUserLike[] = []

    if (collaboratorUsersResult.data != null) {
      const fetchedUsers = [...collaboratorUsersResult.data]
      const ownerIndex = ownerId
        ? fetchedUsers.findIndex(({ uuid }) => uuid === ownerId)
        : -1
      if (ownerIndex > -1) {
        owner = fetchedUsers.splice(ownerIndex, 1)[0]
      }

      // Only collaborators, without the owner
      const userIdsExcludingOwner = new Set(
        accessMetadata?.users.map((user) => user.id) ?? []
      )
      for (const user of fetchedUsers) {
        if (userIdsExcludingOwner.has(user.uuid)) collaborators.push(user)
      }
    }

    if (accessMetadata?.groups.length && allGroupsResult.data != null) {
      const groups = allGroupsResult.data.reduce<PermUserGroup[]>(
        (groups, group) => {
          const { meta } =
            accessMetadata.groups.find(({ id }) => id === group.uuid) ?? {}
          if (meta != null) {
            groups.push({ ...group, count: meta.count })
          }
          return groups
        },
        []
      )
      collaborators.push(...groups)
    }

    return { collaborators, owner }
  }, [
    accessMetadata,
    allGroupsResult.data,
    collaboratorUsersResult.data,
    ownerId,
  ])

  const { inheritanceResources, workspaceResource } = useMemo(() => {
    const resources: InheritanceResource[] =
      readableResourcesAsInheritanceResources(accessMetadata?.resources) ?? []

    // Workspace inheritance is exposed separately and used by the privacy toggle.
    const inheritanceResources = resources.filter(
      ({ resourceType }) => resourceType !== 'workspace'
    )
    const workspaceResource = resources.find(
      ({ resourceType }) => resourceType === 'workspace'
    )

    return { inheritanceResources, workspaceResource }
  }, [accessMetadata])

  const [memberCount, spacesCount] = useMemo(
    () =>
      getMemberCount(
        owner
          ? [...collaborators, ...inheritanceResources, owner]
          : [...collaborators, ...inheritanceResources]
      ),
    [collaborators, inheritanceResources, owner]
  )

  return {
    collaborators,
    currentUser: currentUserQuery.data,
    inheritanceResources,
    isLoading,
    memberCount,
    owner,
    spacesCount,
    workspaceResource,
  }
}
